import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'nat-gateway', label: 'NAT Gateway: The Always-On Trap', level: 2 },
  { id: 'vnet-peering', label: "VNet Peering: What's Free and What Isn't", level: 2 },
  { id: 'public-ip', label: 'Public IP: After Basic SKU Retirement', level: 2 },
  { id: 'ddos', label: 'DDoS Protection: IP vs Network', level: 2 },
  { id: 'calculator', label: 'Networking Calculator', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-700 Designing and Implementing Microsoft Azure Networking Solutions"
    url="https://www.pluralsight.com/paths/az-700-designing-and-implementing-microsoft-azure-networking-solutions"
    price="From £29/month"
    badgeText="Covers VNet, NAT Gateway, DDoS"
  />
);

export default function AzureNetworkingCostsGuide() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Networking Hidden Costs UK 2026',
            description:
              'NAT Gateway always-on billing, VNet peering charges, Public IP Standard SKU costs, and DDoS Protection pricing — in GBP for UK South.',
            author: { '@type': 'Person', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-04-08',
            url: 'https://www.azure-calc.co.uk/guides/azure-networking-costs-uk',
          }),
        }}
      />
      <GuideLayout
        title="Azure Networking Hidden Costs UK 2026"
        description="NAT Gateway costs ~£33/month even idle. VNet peering charges both directions. Public IP Basic SKU is gone. Here's what Azure networking actually costs in UK South."
        lastUpdated="April 2026"
        canonical="https://www.azure-calc.co.uk/guides/azure-networking-costs-uk"
        metaTitle="Azure Networking Hidden Costs UK 2026 | AzureCalc.uk"
        metaDescription="NAT Gateway always-on billing, VNet peering charges, Public IP costs, and DDoS Protection pricing for UK South in GBP."
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'networking', path: '/networking', label: 'Networking Calculator' },
          { service: 'bandwidth', path: '/bandwidth', label: 'Bandwidth / Egress Calculator' },
        ]}
        bottomCta={bottomCta}
      >

        {/* ── Section 1: NAT Gateway ──────────────────────────────── */}
        <h2 id="nat-gateway">NAT Gateway: The Always-On Trap</h2>

        <p>
          Here is the fact that surprises nearly every Azure engineer who deploys NAT Gateway for the
          first time: <strong>there is no stop or pause state</strong>. Unlike a virtual machine that
          you can deallocate, or an application gateway you can scale to zero, a NAT Gateway charges
          every single hour from the moment it is created until the moment it is deleted. Nothing in
          between stops the billing.
        </p>

        <p>
          At UK South rates, an idle NAT Gateway — one with no traffic passing through it whatsoever —
          costs approximately <strong>£33.40/month</strong> (£0.0449/hr × 744 hours). Engineers frequently
          report discovering charges on workloads that were "shut down for the weekend" or on test
          environments they forgot to clean up. Three months later: ~£100 for a gateway that served no traffic.
        </p>

        <h3>Why can't you pause it?</h3>

        <p>
          NAT Gateway is a zone-redundant, regional resource. It maintains a pool of outbound ports
          and SNAT mappings for all resources in the associated subnet. Pausing it would drop all active
          connections and require SNAT port re-allocation when resumed. Microsoft made the architectural
          decision that the complexity of a stopped state wasn't worth it for a component priced at this level.
        </p>

        <h3>The billing model</h3>
        <ul>
          <li><strong>Gateway charge:</strong> ~£0.0449/hr, regardless of traffic. Always billed.</li>
          <li><strong>Data processing charge:</strong> ~£0.0449/GB of data processed through the gateway.</li>
        </ul>

        <p>
          The data processing charge is separate from egress charges. You pay once for the outbound
          internet transfer (via the Bandwidth service) and again for the NAT Gateway processing the same bytes.
        </p>

        <h3>When is NAT Gateway worth it?</h3>
        <p>
          NAT Gateway is genuinely the right choice when you have many resources in a subnet that need
          outbound internet access and you want a predictable, static set of outbound IP addresses (for
          whitelisting). For development/test environments that run part-time, consider using service
          endpoints or outbound rules on a load balancer instead — both avoid the always-on charge.
        </p>

        <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#107c10]">lightbulb</span>
          <p className="text-sm text-on-surface">
            <strong className="text-[#107c10]">Practical tip:</strong> If you only need outbound internet
            access for a few specific services (Azure Monitor, Key Vault, Azure Storage), use Service
            Endpoints instead. They're free and route traffic through the Microsoft backbone.
          </p>
        </div>

        {/* ── Section 2: VNet Peering ─────────────────────────────── */}
        <h2 id="vnet-peering">VNet Peering: What's Free and What Isn't</h2>

        <p>
          The fact that surprises engineers here: <strong>inter-Availability Zone transfer within
          UK South has been free since 2023</strong>. Many cost estimates built before that change are now
          overstating networking costs. If your workload keeps traffic within the same Azure region across
          different AZs, that specific transfer is £0.
        </p>

        <p>
          What <em>is</em> charged:
        </p>

        <ul>
          <li>
            <strong>Same-region VNet peering</strong> (two VNets both in UK South): charged in both
            directions at approximately <strong>£0.015/GB</strong> per side. A 100 GB bidirectional
            workload costs £0.015 × 200 GB = £3/month.
          </li>
          <li>
            <strong>Global VNet peering</strong> (UK South to another region): charged at
            approximately <strong>£0.0263/GB</strong> ingress + <strong>£0.0263/GB</strong> egress.
            A 100 GB bidirectional global peering workload costs £0.0263 × 200 GB = £5.26/month.
          </li>
        </ul>

        <h3>The hub-spoke double-charge trap</h3>

        <p>
          If you're running a hub-spoke topology with a Network Virtual Appliance (NVA) routing
          traffic, VNet peering charges double. Here's why: traffic from a spoke VNet to another
          spoke VNet flows through the hub. That means:
        </p>
        <ol>
          <li>Spoke A to Hub: peering charge (outbound from Spoke A)</li>
          <li>Hub to NVA to Spoke B: peering charge (inbound to Spoke B)</li>
        </ol>
        <p>
          The same bytes are charged twice. If your topology routes 500 GB/month through the hub,
          your actual peering cost is based on 1,000 GB of peering data. The Networking Calculator
          on this site will warn you when both NAT Gateway and VNet peering are enabled together,
          which is a common indicator of a hub-spoke setup.
        </p>

        <h3>How to check your actual peering costs</h3>
        <p>
          In Azure Cost Management, filter by Meter Category = "Virtual Network" and Meter Sub-category
          = "Peering". This will break down same-region vs global peering charges separately. Many
          engineers are surprised to find this is their second-largest networking line item after egress.
        </p>

        {/* ── Section 3: Public IP ──────────────────────────────────── */}
        <h2 id="public-ip">Public IP: After Basic SKU Retirement</h2>

        <p>
          Basic SKU Public IPs were retired on <strong>30 September 2025</strong>. All new Public IP
          addresses must use Standard SKU. If you created Public IPs before September 2025, they have
          been automatically migrated or need manual migration — check your Azure portal if you haven't
          done this yet.
        </p>

        <h3>Standard SKU pricing (UK South, GBP)</h3>
        <ul>
          <li><strong>Static Public IP:</strong> £0.0038/hr → approximately <strong>£2.83/month</strong> (£0.0038 × 744)</li>
          <li><strong>Dynamic Public IP (attached to running resource):</strong> First one is <strong>free</strong>. Additional ones charged at the same hourly rate.</li>
          <li><strong>Dynamic Public IP (unattached/idle):</strong> Charged at the static rate — £0.0038/hr. An unused Public IP that you forgot to delete costs ~£2.83/month.</li>
        </ul>

        <div className="bg-[#7a4f00]/10 border border-[#7a4f00]/40 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#d08700]" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
          <p className="text-sm text-[#c09040]">
            <strong className="text-[#d08700]">Idle IPs still cost money.</strong> Dynamic Public IPs
            that aren't associated with any running resource still incur charges. Run an audit of
            unassociated IPs in your subscription and delete any you don't need.
          </p>
        </div>

        <h3>Global and Anycast Public IPs</h3>
        <p>
          Azure Front Door and Traffic Manager use Global SKU Public IPs (£0.0075/hr). These are
          separate from standard Public IPs and are provisioned automatically when you create those
          services — they appear as a separate line item in your bill.
        </p>

        {/* ── Section 4: DDoS Protection ─────────────────────────── */}
        <h2 id="ddos">DDoS Protection: IP vs Network</h2>

        <p>
          Azure DDoS Protection comes in two tiers with very different pricing models:
        </p>

        <h3>IP Protection (per public IP)</h3>
        <p>
          At approximately <strong>£152.6/IP/month</strong> (£0.2051/hr × 744), IP Protection protects
          individual public IP addresses. It's suitable for small deployments with 1–10 protected IPs.
          The per-IP model makes costs predictable: 3 protected IPs = ~£457.8/month.
        </p>

        <h3>Network Protection (flat monthly)</h3>
        <p>
          At approximately <strong>£2,257/month</strong> (£3.0339/hr × 744), Network Protection is a
          flat-rate plan covering your entire subscription and up to 100 public IPs, plus:
        </p>
        <ul>
          <li>DDoS Rapid Response team access</li>
          <li>Cost protection guarantee (credit back if you scale out during an attack)</li>
          <li>Attack analytics and telemetry</li>
          <li>Azure WAF integration at no additional cost</li>
        </ul>

        <h3>Break-even calculation</h3>
        <p>
          Network Protection becomes cheaper than IP Protection when you're protecting more than
          approximately <strong>15 public IPs</strong> (£2,257 ÷ £152.6/IP ≈ 14.8). If you have
          fewer than 15 exposed endpoints, IP Protection is usually the more cost-effective choice.
          If you need the Rapid Response team or Cost Protection guarantee, Network Protection may be
          worth it regardless of IP count.
        </p>

        <div className="bg-surface-container-lowest border border-outline-variant p-4 rounded-sm my-4">
          <p className="text-[11px] text-outline italic">
            Note: DDoS Protection pricing is not available in the Azure Retail Prices API — rates above
            are sourced from the Azure DDoS Protection pricing page and verified against the Retail Prices
            API where available. Always confirm through the official pricing calculator before budgeting.
          </p>
        </div>

        {/* ── Calculator embed ───────────────────────────────────── */}
        <h2 id="calculator">Estimate Your Networking Costs</h2>

        <CalculatorEmbed service="networking" />

        <p>
          <em>
            Built and verified by an independent Azure engineer.{' '}
            <a href="/methodology" className="text-primary hover:underline">
              See methodology →
            </a>
          </em>
        </p>

      </GuideLayout>
    </>
  )
}
